import { Report } from 'notiflix/build/notiflix-report-aio'
import { useState } from 'react'
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa'
import { performClientSearch } from '../subViews/unifiedSearchViews/shared/clientSearchService'
import { ClientData } from '../subViews/unifiedSearchViews/shared/types'
import styles from './styles.module.scss'
import {
    CLAIM_RULE_LABELS,
    ClaimRule,
    ClaimSummary,
    FIELD_TYPE_LABELS,
    Granularity,
    SelectedClient,
    TemplateRow,
    formatDate,
    hasSignatureField,
    missingForClient,
    pickClaimByRule,
    templateRequirements,
} from './types'

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Normalises a unified-search result into the shape the contracts screens use. */
export const toSelectedClient = (client: ClientData): SelectedClient => {
    const first = (client.firstname || '').trim()
    const last = (client.lastname || '').trim()
    const address = client.current_address || {}
    const line1 = [address.flat, address.house_number, address.house_name, address.street]
        .map((p) => (p || '').toString().trim())
        .filter(Boolean)
        .join(' ')
    const city = (address.town_city || address.city || '').toString().trim()
    const county = (address.county || '').toString().trim()
    const postcode = (address.postcode || '').toString().trim()
    return {
        client_id: client.client_id,
        origin: client.origin,
        name: [first, last].filter(Boolean).join(' ') || 'Unnamed client',
        first_name: first,
        last_name: last,
        email: client.email || '',
        phone: client.telephone || '',
        dob: client.dob || '',
        postcode,
        address: {
            line1,
            city,
            county,
            full:
                (address.raw_address || '').toString().trim() ||
                [line1, city, county, postcode].filter(Boolean).join(', '),
        },
        claims: (client.claims || []).map((c: any) => ({
            case_ref: c.case_ref || '',
            lender: c.lender || '',
            policy_number: c.policy_number || '',
            start_date: c.start_date || '',
            cancelled: Boolean(c.cancelled),
            cfa_signed: Boolean(c.signed ?? c.cfa_signed),
        })),
    }
}

export const clientKey = (client: { origin: string; client_id: number }) => `${client.origin}:${client.client_id}`
export const claimKey = (client: { origin: string; client_id: number }, caseRef: string) =>
    `${clientKey(client)}:${caseRef}`

/* Client search ---------------------------------------------------------- */

interface ClientSearchFormProps {
    onResults: (results: SelectedClient[]) => void
    compact?: boolean
}

export const ClientSearchForm = ({ onResults, compact = false }: ClientSearchFormProps) => {
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [dob, setDob] = useState('')
    const [searching, setSearching] = useState(false)

    const clear = () => {
        setEmail('')
        setPhone('')
        setFirstName('')
        setLastName('')
        setDob('')
    }

    const runSearch = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setSearching(true)
        try {
            const searchResult = await performClientSearch({
                email: email.trim() || undefined,
                phone: phone.trim() || undefined,
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
                dob: dob || undefined,
            })
            if (!searchResult.success) {
                const message =
                    typeof searchResult.errors === 'string'
                        ? searchResult.errors
                        : 'Could not search for clients right now.'
                return Report.failure('Search error', message, 'Okay')
            }
            onResults(searchResult.results.map(toSelectedClient))
        } finally {
            setSearching(false)
        }
    }

    return (
        <form className={compact ? '' : styles.panelContent} onSubmit={runSearch}>
            <div className={styles.formGrid}>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>Email</label>
                    <input
                        className={styles.input}
                        type="text"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="client@example.com"
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>Phone</label>
                    <input
                        className={styles.input}
                        type="text"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+447123456789"
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>First name</label>
                    <input
                        className={styles.input}
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First name"
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>Last name</label>
                    <input
                        className={styles.input}
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last name"
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>
                        Date of birth <span className={styles.fieldMeta}>narrows the match</span>
                    </label>
                    <input className={styles.input} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
                </div>
            </div>
            <div className={styles.panelActions}>
                <button type="button" className={styles.secondaryBtn} onClick={clear}>
                    Clear
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={searching}>
                    {searching ? 'Searching...' : 'Search clients'}
                </button>
            </div>
        </form>
    )
}

interface ClientResultListProps {
    results: SelectedClient[] | null
    onPick: (client: SelectedClient) => void
    pickedKeys?: Set<string>
    pickLabel?: string
}

export const ClientResultList = ({ results, onPick, pickedKeys, pickLabel }: ClientResultListProps) => {
    if (results === null) return null
    if (results.length === 0) return <div className={styles.emptyState}>No clients matched that search.</div>
    return (
        <ul className={styles.searchResults}>
            {results.map((client) => {
                const picked = pickedKeys?.has(clientKey(client))
                return (
                    <li key={clientKey(client)}>
                        <button
                            type="button"
                            className={`${styles.searchResult} ${picked ? styles.selected : ''}`}
                            onClick={() => onPick(client)}
                        >
                            <span className={styles.stackCell}>
                                <span className={styles.stackPrimary}>{client.name}</span>
                                <span className={styles.stackSecondary}>
                                    {client.email || 'no email'}
                                    {client.phone ? ` · ${client.phone}` : ''}
                                    {client.dob ? ` · DOB ${formatDate(client.dob)}` : ''}
                                    {` · ${client.claims.length} ${client.claims.length === 1 ? 'claim' : 'claims'}`}
                                </span>
                            </span>
                            <span className={styles.rowActions}>
                                <span className={`${styles.pill} ${styles.muted}`}>{client.origin}</span>
                                {pickLabel && (
                                    <span className={`${styles.pill} ${picked ? '' : styles.muted}`}>
                                        {picked ? 'Added' : pickLabel}
                                    </span>
                                )}
                            </span>
                        </button>
                    </li>
                )
            })}
        </ul>
    )
}

/* Client card + claims --------------------------------------------------- */

interface ClientDataCardProps {
    client: SelectedClient
    fields: TemplateRow['fields']
    claim?: ClaimSummary | null
}

/** Shows which template fields the record can and cannot satisfy. */
export const ClientDataCard = ({ client, fields, claim }: ClientDataCardProps) => {
    const { dataTypes } = templateRequirements(fields)
    const missing = new Set(missingForClient(fields, client, claim))
    const rows = [
        { label: 'Email', value: client.email },
        { label: 'Phone', value: client.phone },
        { label: 'DOB', value: formatDate(client.dob) },
        { label: 'Address', value: client.address.full },
        { label: 'Postcode', value: client.postcode },
    ]
    return (
        <div className={styles.clientCard}>
            <div className={styles.miniStats}>
                {rows.map((row) => (
                    <div key={row.label} className={styles.miniStat}>
                        <span className={`${styles.miniStatValue} ${row.value ? '' : styles.muted}`}>
                            {row.value || 'not on record'}
                        </span>
                        <span className={styles.miniStatLabel}>{row.label}</span>
                    </div>
                ))}
            </div>
            {dataTypes.length > 0 && (
                <div className={styles.chipRow}>
                    {dataTypes.map((type) => (
                        <span
                            key={type}
                            className={`${styles.chip} ${missing.has(type) ? styles.chipBad : styles.chipGood}`}
                            title={missing.has(type) ? 'Missing from the record' : 'Available'}
                        >
                            {missing.has(type) ? <FaExclamationTriangle /> : <FaCheckCircle />}
                            {FIELD_TYPE_LABELS[type]}
                        </span>
                    ))}
                </div>
            )}
        </div>
    )
}

interface ClaimsTableProps {
    claims: ClaimSummary[]
    mode: 'checkbox' | 'radio' | 'none'
    selected: Set<string>
    onToggle?: (caseRef: string) => void
    emptyMessage?: string
}

export const ClaimsTable = ({ claims, mode, selected, onToggle, emptyMessage }: ClaimsTableProps) => {
    if (claims.length === 0) {
        return <div className={styles.emptyState}>{emptyMessage || 'This client has no claims on record.'}</div>
    }
    return (
        <div className={styles.tableWrap}>
            <table className={`${styles.table} ${styles.compactTable}`}>
                <thead>
                    <tr>
                        {mode !== 'none' && <th className={styles.checkCol} />}
                        <th>Case ref</th>
                        <th>Lender</th>
                        <th>Policy no.</th>
                        <th>Start date</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {claims.map((claim) => {
                        const active = selected.has(claim.case_ref)
                        return (
                            <tr
                                key={claim.case_ref}
                                className={active ? styles.selected : ''}
                                onClick={() => mode !== 'none' && onToggle?.(claim.case_ref)}
                            >
                                {mode !== 'none' && (
                                    <td className={styles.checkCol}>
                                        <input
                                            type={mode}
                                            checked={active}
                                            onChange={() => onToggle?.(claim.case_ref)}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={`Select claim ${claim.case_ref}`}
                                        />
                                    </td>
                                )}
                                <td className={styles.nowrap}>{claim.case_ref || '—'}</td>
                                <td>{claim.lender || '—'}</td>
                                <td>{claim.policy_number || '—'}</td>
                                <td className={styles.nowrap}>
                                    {claim.start_date ? formatDate(claim.start_date) : '—'}
                                </td>
                                <td>
                                    <span className={styles.chipRow}>
                                        {claim.cancelled && (
                                            <span className={`${styles.chip} ${styles.chipBad}`}>Cancelled</span>
                                        )}
                                        <span className={`${styles.chip} ${claim.cfa_signed ? styles.chipGood : ''}`}>
                                            {claim.cfa_signed ? 'CFA signed' : 'CFA unsigned'}
                                        </span>
                                    </span>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

/* Granularity ------------------------------------------------------------ */

interface GranularityControlProps {
    value: Granularity
    onChange: (value: Granularity) => void
    defaultValue: Granularity
    claimRule: ClaimRule
    onClaimRuleChange: (rule: ClaimRule) => void
    needsClaim: boolean
}

export const GranularityControl = ({
    value,
    onChange,
    defaultValue,
    claimRule,
    onClaimRuleChange,
    needsClaim,
}: GranularityControlProps) => (
    <div className={styles.granularityRow}>
        <div className={styles.field}>
            <label className={styles.fieldLabel}>
                Documents
                <span className={styles.fieldMeta}>
                    template default: {defaultValue === 'claim' ? 'one per claim' : 'one per client'}
                </span>
            </label>
            <div className={styles.segmented} role="tablist" aria-label="Send granularity">
                <button
                    type="button"
                    role="tab"
                    aria-selected={value === 'client'}
                    className={`${styles.segment} ${value === 'client' ? styles.active : ''}`}
                    onClick={() => onChange('client')}
                >
                    One per client
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={value === 'claim'}
                    className={`${styles.segment} ${value === 'claim' ? styles.active : ''}`}
                    onClick={() => onChange('claim')}
                >
                    One per claim
                </button>
            </div>
        </div>
        {value === 'client' && needsClaim && (
            <div className={styles.field}>
                <label className={styles.fieldLabel}>
                    Claim to merge <span className={styles.fieldMeta}>the template stamps claim data</span>
                </label>
                <select
                    className={styles.select}
                    value={claimRule}
                    onChange={(e) => onClaimRuleChange(e.target.value as ClaimRule)}
                >
                    {(Object.keys(CLAIM_RULE_LABELS) as ClaimRule[]).map((rule) => (
                        <option key={rule} value={rule}>
                            {CLAIM_RULE_LABELS[rule]}
                        </option>
                    ))}
                </select>
            </div>
        )}
    </div>
)

/* Template picker -------------------------------------------------------- */

interface TemplatePickerProps {
    templates: TemplateRow[]
    loaded: boolean
    value: number | null
    onChange: (templateId: number) => void
    /** When set, each option shows whether this client's record can populate it. */
    client?: SelectedClient | null
    /** Called when a template without a field layout is clicked. */
    onNeedsFields?: (templateId: number) => void
}

export const TemplatePicker = ({ templates, loaded, value, onChange, client, onNeedsFields }: TemplatePickerProps) => {
    if (!loaded) return <div className={styles.emptyState}>Loading templates...</div>
    if (templates.length === 0) {
        return (
            <div className={styles.emptyState}>
                No templates yet — upload a contract PDF under Contract Templates and mark where its fields go.
            </div>
        )
    }
    return (
        <div className={styles.templateList} role="radiogroup" aria-label="Template">
            {templates.map((t) => {
                const sig = hasSignatureField(t.fields)
                const { needsClaim } = templateRequirements(t.fields)
                const active = value === t.id
                const noLayout = t.fields.length === 0
                if (noLayout) {
                    return (
                        <button
                            key={t.id}
                            type="button"
                            role="radio"
                            aria-checked={false}
                            className={`${styles.templateOption} ${styles.templateOptionDisabled}`}
                            onClick={() => onNeedsFields?.(t.id)}
                            title="This template has no field layout yet"
                        >
                            <span className={styles.stackCell}>
                                <span className={styles.stackPrimary}>{t.name}</span>
                                <span className={styles.stackSecondary}>
                                    No field layout yet — open Contract Templates and place its fields to make it
                                    sendable.
                                </span>
                            </span>
                            <span className={styles.templateOptionMark} />
                        </button>
                    )
                }
                let compat: { ok: boolean; text: string } | null = null
                if (client) {
                    if (needsClaim && client.claims.length === 0) {
                        compat = { ok: false, text: 'No claims on record' }
                    } else {
                        const claim = needsClaim ? pickClaimByRule(client.claims, 'latest') : null
                        const missing = missingForClient(t.fields, client, claim)
                        compat =
                            missing.length === 0
                                ? { ok: true, text: 'Ready' }
                                : { ok: false, text: `Missing: ${missing.map((m) => FIELD_TYPE_LABELS[m]).join(', ')}` }
                    }
                }
                return (
                    <button
                        key={t.id}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`${styles.templateOption} ${active ? styles.active : ''}`}
                        onClick={() => onChange(t.id)}
                    >
                        <span className={styles.stackCell}>
                            <span className={styles.stackPrimary}>{t.name}</span>
                            <span className={styles.stackSecondary}>
                                {t.fields.length} {t.fields.length === 1 ? 'field' : 'fields'} ·{' '}
                                {sig ? 'Signing link' : 'PDF attachment'}
                                {needsClaim ? ' · claim data' : ''}
                            </span>
                            {compat && (
                                <span className={`${styles.compatBadge} ${compat.ok ? styles.good : styles.bad}`}>
                                    {compat.ok ? <FaCheckCircle /> : <FaExclamationTriangle />}
                                    {compat.text}
                                </span>
                            )}
                        </span>
                        <span className={styles.templateOptionMark} />
                    </button>
                )
            })}
        </div>
    )
}

/* Custom values ---------------------------------------------------------- */

interface CustomValuesFormProps {
    fields: TemplateRow['fields']
    values: Record<string, string>
    onChange: (values: Record<string, string>) => void
}

export const CustomValuesForm = ({ fields, values, onChange }: CustomValuesFormProps) => {
    const { customFields } = templateRequirements(fields)
    if (customFields.length === 0) return null
    return (
        <div className={styles.formGrid}>
            {customFields.map((field) => (
                <div key={field.id} className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor={`custom-${field.id}`}>
                        {field.label}
                        <span className={styles.fieldRequired}>*</span>
                        <span className={styles.fieldMeta}>page {field.page}</span>
                    </label>
                    <input
                        id={`custom-${field.id}`}
                        className={styles.input}
                        type="text"
                        value={values[field.id] || ''}
                        onChange={(e) => onChange({ ...values, [field.id]: e.target.value })}
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                    />
                </div>
            ))}
        </div>
    )
}

/** Downloads a text blob as a file. */
export const downloadText = (filename: string, text: string, type = 'text/csv;charset=utf-8;') => {
    const blob = new Blob([text], { type })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

export const csvEscape = (value: unknown) => {
    const text = value == null ? '' : String(value)
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}
